/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useState } from "react";
import { ConfigAppSDK } from "@contentful/app-sdk";
import {
    Box,
    Flex,
    FormControl,
    Heading,
    Paragraph,
    Text,
    TextInput,
    TextLink,
} from "@contentful/f36-components";
import { useSDK } from "@contentful/react-apps-toolkit";
import app from "../../package.json";
import {
    DeeplUsageWidget,
    FormalitySelect,
    GlossarySelect,
    PremiumCard,
    SupportedLocales,
    UpdateIndicator,
} from "../components";
import { noGlossaryText } from "../components/GlossarySelect/GlossarySelect";
import {
    AppInstallationParameters,
    Formality,
    GlossaryId,
    isGlossaryEnabled,
    isUpdateNotificationEnabled,
} from "../lib";
import { useFetchDeeplUsage } from "../services";
import { styles } from "./styles";

export const ConfigScreen = () => {
    const { version } = app;
    const [parameters, setParameters] = useState<AppInstallationParameters>({
        deeplApiKey: "",
        formality: "default",
        glossaryId: undefined,
        masked: false,
        installedVersion: undefined,
    });
    const [savedGlossaryId, setSavedGlossaryId] = useState<GlossaryId | undefined>(undefined);

    const sdk = useSDK<ConfigAppSDK>();
    const sdkApiKey = sdk.parameters.installation.deeplApiKey;

    const { data: usage } = useFetchDeeplUsage({ skip: !sdkApiKey });

    const setGlossaryId: React.ChangeEventHandler<HTMLSelectElement> = useCallback(e => {
        setParameters(prev => ({
            ...prev,
            glossaryId:
                e.target.value === noGlossaryText ? undefined : (e.target.value as GlossaryId),
        }));
    }, []);

    const maskHandler = useCallback((toMask: string, masked: boolean): string => {
        if (masked) {
            return toMask.replace(/.{10}$/, "**********");
        }

        return toMask;
    }, []);

    const onConfigure = useCallback(async () => {
        const currentState = await sdk.app.getCurrentState();

        return {
            parameters: {
                ...parameters,
                masked: true,
            },

            targetState: currentState,
        };
    }, [parameters, sdk]);

    useEffect(() => {
        sdk.app.onConfigure(() => onConfigure());
        sdk.app.onConfigurationCompleted(() => setSavedGlossaryId(parameters.glossaryId));
    }, [sdk, onConfigure, parameters, sdkApiKey]);

    useEffect(() => {
        (async () => {
            const currentParameters: AppInstallationParameters | null =
                await sdk.app.getParameters();
            const isInstalled = await sdk.app.isInstalled();

            if (currentParameters) {
                setParameters({
                    ...currentParameters,
                    ...(!isInstalled && { installedVersion: version }),
                });
                setSavedGlossaryId(currentParameters.glossaryId);
            }

            sdk.app.setReady();
        })();
    }, [sdk, version]);

    return (
        <>
            <div css={styles.background} />
            <div css={styles.body}>
                <Heading css={styles.spaced}>About Linguiny</Heading>
                <Paragraph>
                    This app allows you to translate your content to different languages with the
                    help of DeepL.
                </Paragraph>
                <hr css={styles.splitter} />
                <Heading css={styles.spaced}>Configuration</Heading>
                <FormControl>
                    <FormControl.Label isRequired>DeepL API key</FormControl.Label>
                    <TextInput
                        value={maskHandler(parameters.deeplApiKey, parameters.masked)}
                        onChange={e =>
                            setParameters({
                                ...parameters,
                                deeplApiKey: e.target.value,
                                masked: false,
                            })
                        }
                    />
                    <Box marginTop="spacingM">
                        {usage && usage.character_count !== 0 ? (
                            <DeeplUsageWidget />
                        ) : (
                            <Text lineHeight="lineHeightL">
                                You find your API Key on your{" "}
                                <TextLink
                                    href="https://www.deepl.com/your-account/keys"
                                    target="_blank">
                                    DeepL account summary
                                </TextLink>
                                . <br />
                                Don't have an account yet? Sign up{" "}
                                <TextLink
                                    href="https://www.deepl.com/pro#developer"
                                    target="_blank">
                                    here
                                </TextLink>
                                .
                            </Text>
                        )}
                    </Box>
                </FormControl>
                {sdkApiKey && (
                    <>
                        <PremiumCard>
                            <FormalitySelect
                                value={parameters.formality}
                                onChange={e =>
                                    setParameters({
                                        ...parameters,
                                        formality: e.target.value as Formality,
                                    })
                                }
                            />
                            {isGlossaryEnabled && (
                                <GlossarySelect
                                    value={parameters.glossaryId}
                                    onChange={setGlossaryId}
                                    savedValue={savedGlossaryId}
                                />
                            )}
                        </PremiumCard>
                        <hr css={styles.splitter} />
                        <SupportedLocales />
                    </>
                )}
                {isUpdateNotificationEnabled && (
                    <>
                        <hr css={styles.splitter} />
                        <UpdateIndicator />
                    </>
                )}
            </div>
            <Flex css={styles.logoContainer}>
                <Text css={styles.logo}>
                    Built with ❤️ by <TextLink href="https://www.becklyn.com">Becklyn</TextLink>
                </Text>
            </Flex>
        </>
    );
};
